import React, { useState, useCallback, useEffect } from 'react';
import { Table, Button, message, Space, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import EditableDrawer from '../../../../../components/EditableDrawer';
import { DetailData } from '../types';
import TaskDetailFilter, { DetailFilters } from './TaskDetailFilter';
import { ViewMode } from './TaskMonitoringFilter';
import { GENERATE_DAILY_TASK_DETAIL_DOWNLOAD_REPORT, GenerateDailyTaskDetailDownloadReportVariables, GenerateDailyTaskDetailDownloadReportResponse } from '../graphql/mutations/generateDailyTaskDetailDownloadReport';
import { GENERATE_DAILY_TASK_DETAIL_DOWNLOAD_REPORT_FOR_DSP, GenerateDailyTaskDetailDownloadReportForDspVariables, GenerateDailyTaskDetailDownloadReportForDspResponse } from '../graphql/mutations/generateDailyTaskDetailDownloadReportForDsp';
import ParcelsDownloadModal from '../../../parcels/components/ParcelsDownloadModal';
import { DriverFilterOption, toDriverIds } from '../../../../../utils/driverFilterOption';

interface DailyTaskDetailDrawerProps {
    visible: boolean;
    title: string;
    data: DetailData[];
    columns: any[];
    loading?: boolean;
    pagination?: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    } | null;
    onClose: () => void;
    onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortDirection?: 'ASC' | 'DESC';
    onPageChange?: (page: number, pageSize?: number) => void;
    detailFilters?: DetailFilters;
    /** 支持传入完整对象或局部字段；父组件应使用 setDetailFilters(prev => ({ ...prev, ...next })) 合并，避免清空一个下拉时其它字段被误覆盖 */
    onDetailFiltersChange?: (next: DetailFilters | Partial<DetailFilters>) => void;
    warehouses?: Array<{ id: string; name: string }>;
    dsps?: Array<{ id: number; name: string }>;
    areas?: Array<{ id: number; name: string }>;
    drivers?: DriverFilterOption[];
    viewMode?: ViewMode;
    // 下载相关 props
    taskDate?: string;
    warehouseId?: string;
    statusField?: string;
    statusTypeOptions?: Array<{ value: string; label: string }>;
    selectedStatusTypes?: string[];
    onStatusTypesChange?: (statusTypes: string[]) => void;
    lastDetailContext?: { type: string; record: any; filters: any; statusField?: string; statusFields?: string[] } | null;
    currentDspId?: number; // 当前DSP ID（DSP经理角色）
    dspMode?: boolean; // 是否为DSP经理
    showWarehouseSelect?: boolean;
    disableWarehouseSelect?: boolean;
    hideDspSelect?: boolean;
    hideAreaSelect?: boolean;
    hideDriverSelect?: boolean;
    /** 司机远程搜索；传入时司机下拉候选由后端按关键词返回 */
    onDriverSearch?: (keyword: string) => void;
    driverOptionsLoading?: boolean;
}

const DailyTaskDetailDrawer: React.FC<DailyTaskDetailDrawerProps> = ({
    visible,
    title,
    data,
    columns,
    loading = false,
    pagination,
    onClose,
    onSortChange,
    currentSortBy = 'createdAt',
    currentSortDirection = 'DESC',
    onPageChange,
    detailFilters,
    onDetailFiltersChange,
    warehouses = [],
    dsps = [],
    areas = [],
    drivers = [],
    viewMode = 'area',
    taskDate,
    warehouseId,
    statusField,
    statusTypeOptions,
    selectedStatusTypes,
    onStatusTypesChange,
    lastDetailContext,
    currentDspId,
    dspMode = false,
    showWarehouseSelect = false,
    disableWarehouseSelect = false,
    hideDspSelect = false,
    hideAreaSelect = false,
    hideDriverSelect = false,
    onDriverSearch,
    driverOptionsLoading
}) => {
    const { t, i18n } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [generateReportMutation] = useMutation<
        GenerateDailyTaskDetailDownloadReportResponse | GenerateDailyTaskDetailDownloadReportForDspResponse,
        GenerateDailyTaskDetailDownloadReportVariables | GenerateDailyTaskDetailDownloadReportForDspVariables
    >(dspMode ? GENERATE_DAILY_TASK_DETAIL_DOWNLOAD_REPORT_FOR_DSP : GENERATE_DAILY_TASK_DETAIL_DOWNLOAD_REPORT);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    
    // 从localStorage获取上次下载时间
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('dailyTaskDetailDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };
    
    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('dailyTaskDetailDownloadLastTime', time.toString());
    };
    
    // 下载冷却时间倒计时
    useEffect(() => {
        const interval = setInterval(() => {
            const currentTime = Date.now();
            const lastTime = getLastDownloadTime();
            const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
            setCooldownRemaining(Math.ceil(remaining / 1000));
            
            if (remaining <= 0) {
                setDownloadLoading(false);
                localStorage.removeItem('dailyTaskDetailDownloadLastTime');
            }
        }, 1000);
        
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);
    
    // 当抽屉关闭时，重置选中状态
    useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
        }
    }, [visible]);
    
    // 构建导出过滤器
    const buildExportFilter = useCallback((): GenerateDailyTaskDetailDownloadReportVariables => {
        const currentSelectedDsps = detailFilters?.selectedDsps || [];
        const currentSelectedAreas = detailFilters?.selectedAreas || [];
        const currentSelectedDrivers = detailFilters?.selectedDrivers || [];
        
        let selectedDspIds: number[] | undefined;
        if (currentSelectedDsps.length > 0) {
            selectedDspIds = currentSelectedDsps.map(dspName => {
                const dsp = dsps.find((d: any) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[];
        }
        
        let selectedAreaIds: number[] | undefined;
        if (currentSelectedAreas.length > 0) {
            selectedAreaIds = currentSelectedAreas.map(areaName => {
                const area = areas.find((a: any) => a.name === areaName);
                return area ? area.id : null;
            }).filter(id => id !== null) as number[];
        }
        
        const selectedDriverIds = toDriverIds(currentSelectedDrivers);
        
        const statusFieldsFromContext = lastDetailContext?.statusFields;
        return {
            taskDate: taskDate || '',
            statusField: statusField ?? null, // 单选兼容
            statusFields: statusFieldsFromContext && statusFieldsFromContext.length > 0 ? statusFieldsFromContext : undefined,
            warehouseId: warehouseId ? Number(warehouseId) : null,
            warehouseIds: null,
            hubId: null,
            dspIds: selectedDspIds,
            areaIds: selectedAreaIds,
            driverIds: selectedDriverIds,
            cityNames: null,
            ids: null,
            viewMode: viewMode || null,
            timezone: null
        };
    }, [taskDate, warehouseId, statusField, viewMode, detailFilters, dsps, areas, drivers]);
    
    // 下载详情报表
    const handleDownloadDetails = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();
        
        // 检查冷却时间
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.waitBeforeRetry', { remainingTime }));
            return;
        }
        
        // 检查是否正在生成
        if (downloadLoading) {
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.generatingInProgress'));
            return;
        }

        // 无数据时直接返回：后端会把「上游查询结果」拼成单号串下传，
        // 空串会让下游 `if (tokens.isNotEmpty())` 整段跳过，退化为「不按单号过滤」而导出全表订单
        if (selectedRowKeys.length === 0 && (pagination?.totalElements ?? 0) === 0) {
            message.warning(t('wms.exceptionorder.message.noDataToDownload'));
            return;
        }
        
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            
            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // 构建查询过滤器
            let queryVariables: GenerateDailyTaskDetailDownloadReportVariables;
            
            // 如果有选中的行，只下载选中的数据（只使用 ids，忽略其他过滤条件）
            if (selectedRowKeys.length > 0) {
                const statusFieldsFromContext = lastDetailContext?.statusFields;
                queryVariables = {
                    taskDate: taskDate || '',
                    statusField: null,
                    statusFields: statusFieldsFromContext && statusFieldsFromContext.length > 0 ? statusFieldsFromContext : undefined,
                    warehouseId: warehouseId ? Number(warehouseId) : null,
                    warehouseIds: null,
                    hubId: null,
                    ids: selectedRowKeys.map(key => String(key)),
                    dspIds: null,
                    areaIds: null,
                    driverIds: null,
                    cityNames: null,
                    viewMode: viewMode || null,
                    timezone: userTimezone,
                    locale: i18n.language
                };
            } else {
                const filter = buildExportFilter();
                // 确保 statusField 正确传递（从 props 中获取，而不是从 filter 中）
                queryVariables = {
                    ...filter,
                    statusField: statusField ?? filter.statusField, // 优先使用 props 中的 statusField
                    timezone: userTimezone,
                    locale: i18n.language
                };
            }
            
            const response = await generateReportMutation({
                variables: queryVariables
            });
            
            const fileName = dspMode
                ? (response.data as GenerateDailyTaskDetailDownloadReportForDspResponse | undefined)?.generateDailyTaskDetailDownloadReportForDsp
                : (response.data as GenerateDailyTaskDetailDownloadReportResponse | undefined)?.generateDailyTaskDetailDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error(t('wms.delivery.dailyTaskMonitoring.messages.failedToStartGeneration'));
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            let errorMessage = t('wms.delivery.dailyTaskMonitoring.messages.generationFailedTryLater');
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
            setDownloadLoading(false);
        }
    };
    // 处理排序点击
    const handleSortClick = (field: string) => {
        let newDirection: 'ASC' | 'DESC' = 'ASC'; // 首次点击默认为升序
        
        // 如果点击的是当前排序字段，则切换排序方向
        if (currentSortBy === field) {
            newDirection = currentSortDirection === 'DESC' ? 'ASC' : 'DESC';
        }
        
        onSortChange?.(field, newDirection);
    };

    // 渲染排序图标
    const renderSortIcon = (field: string) => {
        if (currentSortBy !== field) {
            return <SortDescendingOutlined className="ml-1 cursor-pointer text-gray-400" />;
        }
        
        return currentSortDirection === 'DESC' 
            ? <SortAscendingOutlined className="ml-1 cursor-pointer text-blue-500" />
            : <SortDescendingOutlined className="ml-1 cursor-pointer text-blue-500" />;
    };

    // 为列添加排序功能
    const enhancedColumns = columns.map(column => {
        if (column.sortable && onSortChange) {
            return {
                ...column,
                title: (
                    <span 
                        className="cursor-pointer select-none"
                        onClick={() => handleSortClick(column.dataIndex)}
                    >
                        {column.title}
                        {renderSortIcon(column.dataIndex)}
                    </span>
                )
            };
        }
        return column;
    });

    // 复制选中行的运单号到剪贴板（参照 delivery/parcels 页面）
    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRowKeys || selectedRowKeys.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const keySet = new Set(selectedRowKeys.map(k => String(k)));
        const trackingNumbers = data
            .filter(record => keySet.has(String(record.id ?? '')) || keySet.has(String(record.tracking_number ?? '')))
            .map(record => (record.tracking_number ?? '').trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRowKeys, data]);

    return (
        <>
            <EditableDrawer
                title={title}
                visible={visible}
                onClose={onClose}
                width={1000}
                showDefaultFooter={false}
            >
                {detailFilters !== undefined && onDetailFiltersChange && (
                    <div className="mb-4 pb-4">
                        <Row gutter={[16, 16]} align="middle">
                            <Col flex="auto">
                                <TaskDetailFilter
                                    filters={detailFilters}
                                    onFiltersChange={onDetailFiltersChange}
                                    warehouses={warehouses}
                                    dsps={dsps}
                                    areas={areas}
                                    drivers={drivers}
                                    viewMode={viewMode}
                                    showWarehouseSelect={showWarehouseSelect}
                                    disableWarehouseSelect={disableWarehouseSelect}
                                    showDspSelect={!hideDspSelect}
                                    showAreaSelect={!hideAreaSelect}
                                    showDriverSelect={!hideDriverSelect}
                                    onDriverSearch={onDriverSearch}
                                    driverOptionsLoading={driverOptionsLoading}
                                    statusTypeOptions={statusTypeOptions}
                                    selectedStatusTypes={selectedStatusTypes}
                                    onStatusTypesChange={onStatusTypesChange}
                                    noWrapper={true}
                                    currentDspId={currentDspId}
                                    dspMode={dspMode}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[16, 4]} align="middle" justify="end" style={{ marginTop: 8 }}>
                            <Col flex="none">
                                <Space direction="vertical" size={4} className="items-end">
                                    <Space>
                                        {selectedRowKeys.length > 0 && (
                                            <span style={{ marginRight: 8 }}>
                                                {t('wms.delivery.deliveryCompletedSummary.selectedCount', { count: selectedRowKeys.length })}
                                            </span>
                                        )}
                                        <Button
                                            type="primary"
                                            onClick={copyTrackingNumbers}
                                            className="w-auto min-w-24"
                                        >
                                            <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={handleDownloadDetails}
                                            disabled={downloadLoading || cooldownRemaining > 0}
                                            loading={downloadLoading}
                                        >
                                            <span className="font-bold">
                                                {cooldownRemaining > 0
                                                    ? `${selectedRowKeys.length > 0 ? t('wms.delivery.deliveryCompletedSummary.exportSelected') : t('components.button.export')} ${cooldownRemaining}s`
                                                    : selectedRowKeys.length > 0
                                                    ? t('wms.delivery.deliveryCompletedSummary.exportSelected')
                                                    : t('components.button.export')}
                                            </span>
                                        </Button>
                                    </Space>
                                    <span className="text-xs text-gray-500">导出的明细字段与parcels保持一致</span>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                )}
                <Table
                    dataSource={data}
                    columns={enhancedColumns}
                    rowKey={(record) => record.id || record.tracking_number}
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => {
                            setSelectedRowKeys(keys);
                        }
                    }}
                    pagination={pagination ? {
                        current: pagination.page,
                        pageSize: pagination.size,
                        total: pagination.totalElements,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                        onChange: onPageChange
                    } : {
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => t('wms.delivery.dailyTaskMonitoring.detailDrawer.totalRecords', { total })
                    }}
                    scroll={{ x: 800 }}
                    size="small"
                />
            </EditableDrawer>
            {/* 下载完成弹窗 */}
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </>
    );
};

export default DailyTaskDetailDrawer;
